import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Shield } from "lucide-react";
import LogoText from "@/components/LogoText";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        // Registration flow
        if (password !== confirmPassword) {
          toast({
            title: "Error",
            description: "Las contraseñas no coinciden",
            variant: "destructive"
          });
          return;
        }

        if (password.length < 6) {
          toast({
            title: "Error",
            description: "La contraseña debe tener al menos 6 caracteres",
            variant: "destructive"
          });
          return;
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/admin/dashboard`
          }
        });

        if (error) throw error;

        toast({
          title: "Registro exitoso",
          description: "Se ha creado tu cuenta de administrador. Puedes iniciar sesión ahora.",
        });

        // Switch to login mode
        setIsRegistering(false);
        setConfirmPassword("");
      } else {
        // Login flow
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // Check if user is admin via RPC
        const { data: isAdminRes, error: isAdminErr } = await supabase
          .rpc('is_admin', { _uid: data.user.id });

        if (isAdminErr) {
          throw isAdminErr;
        }

        if (!isAdminRes) {
          // Try to promote this user if it's the first admin
          const { data: promoted, error: promoteErr } = await supabase
            .rpc('grant_admin_if_none', { _uid: data.user.id });

          if (promoteErr) {
            throw promoteErr;
          }

          // Recheck admin
          const { data: isAdminNow } = await supabase
            .rpc('is_admin', { _uid: data.user.id });

          if (!isAdminNow) {
            await supabase.auth.signOut();
            toast({
              title: "Acceso denegado",
              description: "No tienes permisos de administrador",
              variant: "destructive"
            });
            return;
          }
        }

        toast({
          title: "Bienvenido",
          description: "Has iniciado sesión correctamente",
        });

        navigate('/admin/dashboard');
      }
    } catch (error: any) {
      toast({
        title: isRegistering ? "Error al registrarse" : "Error al iniciar sesión",
        description: error.message || "Verifica tus credenciales",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 bg-primary/10 rounded-2xl">
              <Shield className="h-12 w-12 text-primary" />
            </div>
          </div>
          <div>
            <LogoText />
            <h2 className="text-2xl font-bold mt-2">Panel de Administración</h2>
            <p className="text-muted-foreground">
              {isRegistering ? "Crea tu cuenta de administrador" : "Accede a tu cuenta de administrador"}
            </p>
          </div>
        </div>

        {/* Login/Register Form */}
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-center">
              {isRegistering ? "Crear Cuenta" : "Iniciar Sesión"}
            </CardTitle>
            <CardDescription className="text-center">
              {isRegistering 
                ? "Registra tu primera cuenta de administrador" 
                : "Ingresa tus credenciales de administrador"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@traveltours.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Ingresa tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-11 w-11 px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {isRegistering && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirma tu contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-11 pr-10"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>{isRegistering ? "Creando cuenta..." : "Ingresando..."}</span>
                  </div>
                ) : (
                  isRegistering ? "Crear Cuenta de Admin" : "Ingresar al Panel"
                )}
              </Button>

              {/* Toggle between login and register */}
              <div className="text-center pt-4">
                <Button 
                  type="button"
                  variant="ghost" 
                  onClick={() => {
                    setIsRegistering(!isRegistering);
                    setConfirmPassword("");
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {isRegistering 
                    ? "¿Ya tienes cuenta? Inicia sesión" 
                    : "¿Primera vez? Crear cuenta de administrador"
                  }
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground"
          >
            ← Volver al sitio web
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;